import { ClientService, ToastMessageService } from '@fitness-force/api';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  Validators,
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
} from '@angular/forms';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject } from 'rxjs';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';

@Component({
  selector: 'block-client-booking',
  templateUrl: './block-client-booking.component.html',
  styleUrls: ['./block-client-booking.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    DatePickerModule,
    ShortNamePipe,
    TranslateModule,
    ErrorMsgComponent,
  ],
})
export class BlockClientBookingComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  translate = inject(TranslateService);
  private clientService = inject(ClientService);
  private toastMessageService = inject(ToastMessageService);
  private cd = inject(ChangeDetectorRef);

  readonly blockClientBookingModal = input<boolean | null>(false);
  readonly clientData$ = input<any>(new BehaviorSubject(null));
  readonly isBlocked = input<boolean>(false);
  readonly closeBlockClientBookingDialog = output<{
    close: boolean;
    action: string | null;
  }>();

  localIsBlocked: boolean = false;

  blockClientBookingFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  allClientData: any;
  btnLoading: boolean = false;
  minDate: Date = new Date();

  ngOnInit(): void {
    this.componentInit();
  }

  componentInit() {
    this.blockClientBookingFormGroup = this.fb.group({
      blockUntilDate: ['', Validators.required],
      clientName: [],
    });

    const clientDataSubject = this.clientData$();
    if (clientDataSubject) {
      clientDataSubject.subscribe((client: any) => {
        this.allClientData = client;
        this.ClientName.setValue(this.allClientData.full_name);

        // Check if client is already blocked for booking
        if (this.allClientData.online_booking_stopped_until) {
          const blockDate = new Date(
            this.allClientData.online_booking_stopped_until,
          );
          const today = new Date();
          this.localIsBlocked = blockDate > today;
        }
      });
    }
  }

  closeDialogEvent(action: string | null = null) {
    this.closeBlockClientBookingDialog.emit({ close: true, action });
  }

  blockClientBooking() {
    if (this.blockClientBookingFormGroup.invalid) {
      this.blockClientBookingFormGroup.markAllAsTouched();
      return;
    }

    this.btnLoading = true;
    const selectedDate = this.BlockUntilDate.value;

    // Convert to UTC format as required by API
    const utcDateString = this.convertToUTCString(selectedDate);

    const patchRequest = [
      {
        op: 'replace',
        path: '/OnlineBookingStoppedUntil',
        value: utcDateString,
      },
    ];

    let tenantId = 0;
    if (
      this.allClientData.home_club_tenant_id &&
      this.allClientData.home_club_tenant_id > 0
    ) {
      tenantId = this.allClientData.home_club_tenant_id;
    } else {
      tenantId = this.allClientData.createdin_tenant_id;
    }

    this.clientService
      .patchClient(this.allClientData.id, tenantId, patchRequest)
      .subscribe({
        next: (res: any) => {
          this.translate
            .get('CLIENT_PROFILE.BLOCK_CLIENT_BOOKING.BLOCKED_SUCCESS')
            .subscribe((translations: any) => {
              this.toastMessageService.showSuccess(translations);
            });
          this.btnLoading = false;
          this.closeDialogEvent('blocked');
          this.cd.detectChanges();
        },
        error: (error) => {
          this.btnLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  convertToUTCString(date: Date): string {
    // Format: "MM/DD/YYYY HH:mm:ss AM/PM"
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    const year = date.getUTCFullYear();

    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const seconds = date.getUTCSeconds().toString().padStart(2, '0');

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const hoursStr = hours.toString().padStart(2, '0');

    return `${month}/${day}/${year} ${hoursStr}:${minutes}:${seconds} ${ampm}`;
  }

  get BlockUntilDate(): UntypedFormControl {
    return this.blockClientBookingFormGroup.get(
      'blockUntilDate',
    ) as UntypedFormControl;
  }

  get ClientName(): UntypedFormControl {
    return this.blockClientBookingFormGroup.get(
      'clientName',
    ) as UntypedFormControl;
  }
}
